<template>
  <div class="space-y-4">
    <p class="text-2xl font-medium">
      {{ t('merchantConsultation') }}
    </p>
    <Checkbox v-model="merchantHasBeenConsulted">{{ t('merchantHasBeenConsulted') }}</Checkbox>
    <Information>
      <template #text>
        <p class="font-medium">
          {{ t('deliveryDateAccordingToContract') }}:
          {{ formattedAvailableFromDate }}
        </p>
      </template>
      <template #modal>{{ t('ifDeliveryDateChanges') }}</template>
    </Information>
    <div class="grid grid-cols-3 gap-4">
      <DatepickerField name="availableFrom" :label="t('enterNewDeliveryDate')" />
    </div>
    <Divider />
    <div class="flex justify-end">
      <CVButton
        variant="default"
        :disabled="!merchantHasBeenConsulted"
        :is-loading="isUpdatingCar"
        @click.prevent="submit"
      >
        {{ t('confirmConsultation') }}
      </CVButton>
    </div>
  </div>
</template>

<script lang="ts" setup>
import Checkbox from '@/components/Checkbox.vue';
import type { Car } from '@/entities/cars/car.entity';
import { getDateFormat } from '@/hooks/use-date-format';
import { useFormattedDate } from '@/hooks/use-formatted-date';
import { useI18n } from 'vue-i18n';
import { computed, ref } from 'vue';
import Information from '@/components/Information.vue';
import Divider from '@/components/Divider.vue';
import { useForm } from 'vee-validate';
import { useUpdateCar } from '@/queries/use-cars';
import * as yup from 'yup';
import { toTypedSchema } from '@vee-validate/yup';
import { dateOrStringRequired } from '@/validation/car.schema';
import DatepickerField from '@/components/DatepickerField.vue';

const props = defineProps<{
  car: Car;
}>();

const emit = defineEmits<{
  (e: 'complete'): void;
}>();

const { t } = useI18n();

const merchantHasBeenConsulted = ref(false);
const formattedAvailableFromDate = useFormattedDate(
  computed(() => props.car.availableFrom),
  getDateFormat(),
);

const { mutateAsync: updateCar, isPending: isUpdatingCar } = useUpdateCar();

const { handleSubmit } = useForm({
  initialValues: {
    availableFrom: undefined,
  },
  validationSchema: toTypedSchema(
    yup.object({
      availableFrom: dateOrStringRequired(),
    }),
  ),
});

const submit = handleSubmit(async (values) => {
  await updateCar({
    id: props.car.id,
    car: {
      merchantConsultedDate: new Date().toISOString(),
      availableFrom: new Date(values.availableFrom).toISOString(),
    },
  });
  emit('complete');
});
</script>

<i18n lang="json">
{
  "en": {
    "merchantConsultation": "Merchant Consultation",
    "ifDeliveryDateChanges": "If the delivery date changes significantly, be sure to consult because of legal claims!",
    "merchantHasBeenConsulted": "Merchant has been consulted",
    "deliveryDateAccordingToContract": "Delivery date according to contract",
    "enterNewDeliveryDate": "Enter new delivery date",
    "confirmConsultation": "Confirm consultation"
  },
  "de": {
    "merchantConsultation": "Händler Rücksprache",
    "ifDeliveryDateChanges": "Wenn sich der Liefertermin stark verändert, unbedingt Rücksprache halten, wegen rechtlichen Ansprüchen!",
    "merchantHasBeenConsulted": "Händler Rücksprache wurde gehalten",
    "deliveryDateAccordingToContract": "Liefertermin laut Vertrag",
    "enterNewDeliveryDate": "Liefertermin neu eingeben",
    "confirmConsultation": "Rücksprache bestätigen"
  },
  "es": {
    "merchantConsultation": "Consulta con concesionario",
    "ifDeliveryDateChanges": "Si la fecha de entrega cambia mucho, coordina con el concesionario por posibles reclamaciones legales.",
    "merchantHasBeenConsulted": "Se ha hablado con el concesionario",
    "deliveryDateAccordingToContract": "Fecha de entrega según contrato",
    "enterNewDeliveryDate": "Introducir nueva fecha de entrega",
    "confirmConsultation": "Confirmar consulta"
  }
}
</i18n>
